import { PieChart, HeartPulse, Trophy, HandHeart, Building2, ShieldCheck, ArrowRight } from "lucide-react";
import { useDonation } from "@/context/DonationContext";

// TODO (equipe): atualizar percentuais quando a prestação de contas oficial
// do trimestre for publicada. Os valores abaixo refletem a distribuição
// aproximada atual conforme o rulebook institucional.
const ALLOCATIONS = [
  {
    id: "enaga",
    label: "ENAGA (Saúde e TEA)",
    percent: 50,
    desc: "Terapias, equipe multidisciplinar e material clínico.",
    icon: HeartPulse,
    color: "#3B82F6",
  },
  {
    id: "esporte",
    label: "Esporte",
    percent: 25,
    desc: "Escolinhas de base, uniformes, torneios e professores.",
    icon: Trophy,
    color: "#10B981",
  },
  {
    id: "social",
    label: "Projetos Sociais",
    percent: 15,
    desc: "Cestas básicas, reforço escolar e acolhimento de famílias.",
    icon: HandHeart,
    color: "#F59E0B",
  },
  {
    id: "estrutura",
    label: "Estrutura",
    percent: 10,
    desc: "Manutenção de quadras, piscinas e centro terapêutico.",
    icon: Building2,
    color: "#8B5CF6",
  },
];

/**
 * Donut chart construído em SVG puro (sem libs externas).
 * Cada fatia é um círculo com stroke-dasharray proporcional ao percentual.
 */
function DonutChart({ size = 220, stroke = 28 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  let offsetAcc = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
      {/* Background ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#E2E8F0"
        strokeWidth={stroke}
      />
      {ALLOCATIONS.map((a) => {
        const dash = (a.percent / 100) * circumference;
        const gap = circumference - dash;
        const startRotation = (offsetAcc / 100) * 360 - 90;
        offsetAcc += a.percent;
        return (
          <circle
            key={a.id}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={a.color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`}
            strokeLinecap="butt"
            transform={`rotate(${startRotation} ${size / 2} ${size / 2})`}
          />
        );
      })}
      {/* Center label */}
      <text
        x={size / 2}
        y={size / 2 - 4}
        textAnchor="middle"
        className="fill-ga-ink"
        style={{ fontFamily: "Outfit, sans-serif", fontWeight: 900, fontSize: 28 }}
      >
        100%
      </text>
      <text
        x={size / 2}
        y={size / 2 + 18}
        textAnchor="middle"
        className="fill-ga-ink/60"
        style={{ fontFamily: "Manrope, sans-serif", fontSize: 11, letterSpacing: 2 }}
      >
        DA DOAÇÃO
      </text>
    </svg>
  );
}

export default function ParaOndeVai() {
  const { openModal } = useDonation();
  return (
    <section
      id="para-onde-vai"
      data-testid="para-onde-vai-section"
      className="section-pad bg-white"
    >
      <div className="container-ga">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="pill border-ga-ink/10 text-ga-ink/70 bg-ga-ink/5 mb-5" data-testid="para-onde-vai-pill">
              <PieChart className="w-3.5 h-3.5" />
              Transparência
            </div>
            <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-ga-ink">
              Para onde vai a sua <span className="gradient-text-ga">doação</span>?
            </h2>
            <p className="mt-5 text-ga-ink/65 text-base sm:text-lg">
              Cada real doado é aplicado diretamente nas frentes de atuação do
              Instituto, auditado e publicado em prestação de contas
              trimestral.
            </p>
          </div>
          <span
            data-testid="para-onde-vai-selo"
            className="self-start inline-flex items-center gap-2 rounded-full bg-ga-emerald/10 text-ga-emerald_deep px-4 py-2 text-xs font-bold uppercase tracking-[0.18em]"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Prestação de contas pública
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Donut */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-start">
            <div className="rounded-[2rem] bg-background border border-border p-8 w-full flex items-center justify-center" data-testid="para-onde-vai-chart">
              <DonutChart size={260} stroke={34} />
            </div>
            <p className="mt-4 text-xs text-ga-ink/55 max-w-xs">
              Distribuição média dos recursos recebidos em 2025. Valores
              aproximados, atualizados trimestralmente.
            </p>
          </div>

          {/* Breakdown list */}
          <ul className="lg:col-span-7 space-y-3" data-testid="para-onde-vai-list">
            {ALLOCATIONS.map((a) => (
              <li
                key={a.id}
                data-testid={`alocacao-${a.id}`}
                className="rounded-3xl border border-border bg-white p-5 sm:p-6"
              >
                <div className="flex items-start gap-4">
                  <span
                    className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-white"
                    style={{ background: a.color }}
                  >
                    <a.icon className="w-5 h-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <h3 className="font-display font-bold text-lg text-ga-ink">
                        {a.label}
                      </h3>
                      <span
                        className="font-display font-black text-2xl"
                        style={{ color: a.color }}
                      >
                        {a.percent}%
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-ga-ink/65 leading-relaxed">
                      {a.desc}
                    </p>
                    {/* Progress bar */}
                    <div className="mt-3 h-2 rounded-full bg-ga-ink/5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-[width] duration-700"
                        style={{ width: `${a.percent}%`, background: a.color }}
                      />
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 rounded-[2rem] bg-ga-ink text-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-ga-emerald/25 blur-3xl" />
          <div className="relative flex-1">
            <h3 className="font-display font-bold text-2xl sm:text-3xl leading-tight">
              Sua doação vira <span className="text-ga-amber">impacto mensurável</span>.
            </h3>
            <p className="mt-2 text-white/75 text-sm sm:text-base max-w-xl">
              100% dos recursos são aplicados nos projetos acima. Os custos
              administrativos são cobertos por parceiros e não pelas doações
              individuais.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openModal("para-onde-vai")}
            data-testid="para-onde-vai-cta"
            className="relative shrink-0 inline-flex items-center gap-2 rounded-full bg-ga-emerald text-white font-bold py-4 px-7 hover:bg-ga-emerald_deep transition-colors shadow-lg"
          >
            Doar com Pix
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
